import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Package, TrendingUp, Receipt, BarChart3, Sun, Moon, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/83824f35-3ffa-4a4b-99df-eb320d1f49ec/images/f7e8960f40cd8a0232b0979111e2b7626e4de012be8e45f7731b894971bd151a.png";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/83824f35-3ffa-4a4b-99df-eb320d1f49ec/images/96cf2d3e8dbc6ee0ba30613b713282c80a130d6a715be7bc5deae1702f6184d6.png";

const features = [
  { icon: Package, title: 'Inventory Management', desc: 'Track every phone — brand, model, condition, purchase price, and stock status at a glance.' },
  { icon: TrendingUp, title: 'Sales Tracking', desc: 'Record every sale with automatic profit calculations. Know which phones earn the most.' },
  { icon: Receipt, title: 'Expense Tracking', desc: 'Log repairs, shipping, accessories, and other costs to see your true net profit.' },
  { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Revenue trends, top sellers, profit margins — all visualized with beautiful charts.' },
];

export default function LandingPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} className="h-8 w-8 rounded-lg" alt="FlipTrack" />
            <span className="font-heading text-xl font-semibold">FlipTrack</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="landing-theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-signin-btn">
              Sign in
            </Button>
            <Button onClick={() => navigate('/register')} data-testid="login-btn" className="gap-2">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-[0.2em] mb-6">
              Phone Reselling Made Easy
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">
              Track Every Flip.<br />
              <span className="text-primary">Maximize Every Profit.</span>
            </h1>
            <p className="text-base leading-relaxed text-muted-foreground mb-8 max-w-lg">
              The all-in-one platform for phone resellers. Manage inventory, track sales, monitor expenses, and watch your profits grow.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" onClick={() => navigate('/register')} className="gap-2" data-testid="hero-cta-btn">
                Get Started <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-learn-btn">Learn More</Button>
            </div>
          </div>
          <div className="animate-fade-in-up-delay-2 relative">
            <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-2xl" />
            <img src={HERO_IMG} alt="Phone reselling setup" className="relative rounded-2xl shadow-2xl w-full" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-muted/50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
              FlipTrack gives you the tools to run a professional phone reselling business.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <Card key={i} className={`border border-border bg-card hover:border-primary/50 transition-all duration-200 animate-fade-in-up-delay-${i+1}`} data-testid={`feature-card-${i}`}>
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <f.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-8 text-center">
          <div>
            <p className="font-heading text-3xl sm:text-4xl font-semibold text-primary">100%</p>
            <p className="text-sm text-muted-foreground mt-2">Profit Visibility</p>
          </div>
          <div>
            <p className="font-heading text-3xl sm:text-4xl font-semibold text-primary">Real-time</p>
            <p className="text-sm text-muted-foreground mt-2">Inventory Tracking</p>
          </div>
          <div>
            <p className="font-heading text-3xl sm:text-4xl font-semibold text-primary">Zero</p>
            <p className="text-sm text-muted-foreground mt-2">Hidden Costs</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-muted/50" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-6">
            Ready to Scale Your Business?
          </h2>
          <p className="text-muted-foreground text-base mb-8">
            Join phone resellers who are already using FlipTrack to grow their profits.
          </p>
          <Button size="lg" onClick={() => navigate('/register')} className="gap-2" data-testid="cta-btn">
            Start Tracking Now <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-muted-foreground">
          <span className="flex items-center gap-2">
            <img src={LOGO_URL} className="h-5 w-5 rounded" alt="" />
            FlipTrack {new Date().getFullYear()}
          </span>
          <span>Built for phone resellers</span>
        </div>
      </footer>
    </div>
  );
}
